import React from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { Button, Space, Dropdown, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
    EBillingStatus,
    EBillingStatusLabel,
    EChargeType,
    EChargeTypeLabel,
    TPaymentBilling,
    TBillingFrom,
} from 'types/provider';
import usePatientPaymentBillingList from 'hooks/usePatientPaymentBillingList';
import StatusLabel from 'components/ManageSubscription/components/StatusLabel';
import { ColumnType } from 'antd/es/table';
import { getChargeTypeLabel } from 'utils/common';
import CommonTable from 'components/CommonTable';
import PaymentEventModal from 'components/ManageSubscription/components/PaymentEventModal';
import { TGetPaymentBillingList } from 'api/provider';
import useMyEarning from 'hooks/useMyEarning';
import CancelInvoiceModal from './components/CancelInvoiceModal';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import InvoiceReminderBtn from './components/InvoiceReminderBtn';
import InvoiceLabel from './components/InvoiceLabel';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import ProviderCell from 'components/ProviderCell';
import RefundModal, { canIssueRefund } from 'components/RefundModal';

// STATUS filter options follow the labels shown in the table (one option per label, covering
// every status the list can return); each option is sent as every backend status with that label
const STATUS_FILTER_OPTIONS: { label: string; statuses: EBillingStatus[] }[] = [
    { label: EBillingStatusLabel.SCHEDULED, statuses: [EBillingStatus.SCHEDULED] },
    { label: EBillingStatusLabel.SENT, statuses: [EBillingStatus.SENT] },
    { label: EBillingStatusLabel.OVERDUE, statuses: [EBillingStatus.OVERDUE] },
    { label: EBillingStatusLabel.DISPUTE_OPEN, statuses: [EBillingStatus.DISPUTE_OPEN] },
    { label: EBillingStatusLabel.PENDING, statuses: [EBillingStatus.PENDING] },
    { label: EBillingStatusLabel.FAILED, statuses: [EBillingStatus.FAILED] },
    { label: EBillingStatusLabel.CANCELED, statuses: [EBillingStatus.CANCELED, EBillingStatus.BILLING_CANCELED] },
    { label: EBillingStatusLabel.PROCESSING, statuses: [EBillingStatus.PROCESSING] },
    { label: EBillingStatusLabel.SUCCEEDED, statuses: [EBillingStatus.SUCCEEDED, EBillingStatus.FULLY_PAID] },
    { label: EBillingStatusLabel.PARTIAL_PAID, statuses: [EBillingStatus.PARTIAL_PAID] },
    { label: EBillingStatusLabel.REFUND_PROCESSING, statuses: [EBillingStatus.REFUND_PROCESSING] },
    { label: EBillingStatusLabel.REFUNDED, statuses: [EBillingStatus.REFUNDED] },
    { label: EBillingStatusLabel.PARTIAL_REFUNDED, statuses: [EBillingStatus.PARTIAL_REFUNDED] },
    { label: EBillingStatusLabel.REFUND_FAILED, statuses: [EBillingStatus.REFUND_FAILED] },
    { label: EBillingStatusLabel.DISPUTE_PROCESSING, statuses: [EBillingStatus.DISPUTE_PROCESSING] },
    { label: EBillingStatusLabel.DISPUTE_WON, statuses: [EBillingStatus.DISPUTE_WON] },
    { label: EBillingStatusLabel.DISPUTE_LOST, statuses: [EBillingStatus.DISPUTE_LOST] },
    {
        label: EBillingStatusLabel.PATIENT_NOT_READY,
        statuses: [EBillingStatus.PATIENT_NOT_READY, EBillingStatus.PROVIDER_NOT_READY],
    },
];

// Column dataIndex -> backend orderKey
const SORT_ORDER_KEYS: Record<string, TGetPaymentBillingList['orderKey']> = {
    createdAt: 'CREATE_AT',
    amountCents: 'AMOUNT',
    payoutAmount: 'PAYOUT',
    issueDate: 'ISSUE_DATE',
    dueDate: 'DUE_DATE',
};

// Everything the list request depends on; any change refetches with all of it
interface TQuery {
    current: number;
    filters: Record<string, string[] | null>;
    sorter: any;
    patientName: string;
    timeRangeMonths?: number;
    clientId?: number;
}

interface Props {
    clientId?: number;
    timeRangeMonths?: number;
    isGroupPractice?: boolean;
}

const PaymentTable = ({ clientId, timeRangeMonths, isGroupPractice }: Props) => {
    const [myEarning] = useMyEarning();
    const [searchNameText, setSearchNameText] = React.useState('');
    const [searchDescriptionText, setSearchDescriptionText] = React.useState('');
    const [query, setQuery] = React.useState<TQuery>(() => ({
        current: 1,
        filters: {},
        sorter: {},
        patientName: '',
        timeRangeMonths,
        clientId,
    }));
    const [currentItem, setCurrentItem] = React.useState<TPaymentBilling>();
    const [showViewTransactionModal, setShowViewTransactionModal] = React.useState(false);
    const [showIssueRefundModal, setShowIssueRefundModal] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [showCancelInvoiceModal, setShowCancelInvoiceModal] = React.useState(false);
    const [showInvoiceDetailModal, setShowInvoiceDetailModal] = React.useState(false);
    const [getDataUpdatedFlag] = DataUpdatedFlagStore.useStore();
    const billingDataUpdatedFlag = getDataUpdatedFlag('billingDataUpdated');

    const [paymentBillingList, paymentBillingListTotal, loading, refetch] = usePatientPaymentBillingList(
        undefined,
        isGroupPractice,
        true,
    );

    // Time range / client come from props: fold them into the query and go back to page 1.
    // Same values (e.g. on mount) return the same object, so no extra request.
    React.useEffect(() => {
        setQuery((q) =>
            q.timeRangeMonths === timeRangeMonths && q.clientId === clientId
                ? q
                : { ...q, timeRangeMonths, clientId, current: 1 },
        );
    }, [timeRangeMonths, clientId]);

    // 处理表格变化
    const handleTableChange = (pagination: any, filters: any, sorter: any, extra: { action: string }) => {
        setQuery((q) => ({
            ...q,
            current: extra.action === 'paginate' ? pagination.current : 1,
            filters,
            sorter,
        }));
    };

    const buildParams = (q: TQuery) => {
        const filter: Partial<TGetPaymentBillingList> = {};
        filter.current = q.current;
        const orderKey = q.sorter?.order && SORT_ORDER_KEYS[q.sorter.field];
        if (orderKey) {
            filter.orderKey = orderKey;
            filter.orderType = q.sorter.order === 'ascend' ? 'ASC' : 'DESC';
        }
        if (q.clientId) {
            filter.clientId = q.clientId;
        }
        if (q.timeRangeMonths !== undefined) {
            filter.timeRangeMonths = q.timeRangeMonths;
        }
        if (q.filters.billingStatus && q.filters.billingStatus.length > 0) {
            const selected = q.filters.billingStatus;
            filter.billingStatus = STATUS_FILTER_OPTIONS.filter((o) => selected.includes(o.label)).flatMap(
                (o) => o.statuses,
            );
        }
        if (q.filters.chargeType && q.filters.chargeType.length > 0) {
            filter.chargeType = q.filters.chargeType;
        }
        if (q.patientName) {
            filter.patientName = q.patientName;
        }
        return filter;
    };

    const refetchData = () => {
        refetch(buildParams(query));
    };

    React.useEffect(() => {
        refetchData();
    }, [query]);

    // Only react to updates after mount; the mount itself is covered by the query effect
    const lastBillingFlag = React.useRef(billingDataUpdatedFlag);
    React.useEffect(() => {
        if (billingDataUpdatedFlag && billingDataUpdatedFlag !== lastBillingFlag.current) {
            refetchData();
        }
        lastBillingFlag.current = billingDataUpdatedFlag;
    }, [billingDataUpdatedFlag]);

    const isInvoice = (item: TPaymentBilling) => {
        return item.billingFrom === TBillingFrom.KLARITY_USER_INVOICE;
    };

    const allColumns: ColumnType<TPaymentBilling>[] = [
        {
            title: 'NAME',
            dataIndex: 'providerFirstName',
            key: 'providerFirstName',
            width: 160,
            render: (name: string, item: TPaymentBilling) => (
                <div>
                    <div className={s.providerName}>
                        {item.patientFirstName} {item.patientLastName}
                    </div>
                </div>
            ),
            filterDropdown: (props: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search by name"
                        value={searchNameText}
                        onChange={(e) => setSearchNameText(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setQuery((q) => ({ ...q, patientName: searchNameText, current: 1 }));
                                props.confirm();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setSearchNameText('');
                                setQuery((q) => ({ ...q, patientName: '', current: 1 }));
                                props.clearFilters();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{
                        color: filtered || searchDescriptionText ? '#1890ff' : undefined,
                    }}
                />
            ),
            onFilter: () => true, // 我们使用自己的过滤逻辑
        },
        {
            title: 'CHARGE TYPE',
            dataIndex: 'chargeType',
            key: 'chargeType',
            width: 150,
            filters: [
                { text: EChargeTypeLabel.NO_SHOW, value: EChargeType.NO_SHOW },
                {
                    text: EChargeTypeLabel.LATE_CANCELLATION,
                    value: EChargeType.LATE_CANCELLATION,
                },
                {
                    text: EChargeTypeLabel.RESCHEDULE,
                    value: EChargeType.RESCHEDULE,
                },
                {
                    text: EChargeTypeLabel.LEAD_GEN_FEE,
                    value: EChargeType.LEAD_GEN_FEE,
                },
                {
                    text: EChargeTypeLabel.PARTIAL_PAYMENT,
                    value: EChargeType.PARTIAL_PAYMENT,
                },
                {
                    text: EChargeTypeLabel.FULL_SELF_PAYMENT,
                    value: EChargeType.FULL_SELF_PAYMENT,
                },
                { text: EChargeTypeLabel.TEXT_VISIT, value: EChargeType.TEXT_VISIT },
                { text: EChargeTypeLabel.CO_PAY, value: EChargeType.CO_PAY },
                { text: EChargeTypeLabel.OTHER, value: EChargeType.OTHER },
                { text: EChargeTypeLabel.INVOICE, value: EChargeType.INVOICE },
            ],
            filteredValue: query.filters.chargeType || null,
            onFilter: (value: any, record: TPaymentBilling) => {
                return true;
            },
            render: (chargeType: EChargeType, record: TPaymentBilling) => {
                if (isInvoice(record)) {
                    return <InvoiceLabel record={record} />;
                }
                return <div className={s.tableItem}>{getChargeTypeLabel(chargeType)}</div>;
            },
        },
        ...(isGroupPractice
            ? [
                  {
                      title: 'PROVIDER',
                      key: 'providerName',
                      width: 150,
                      render: (_: string, record: TPaymentBilling) => {
                          const name =
                              record.providerName ||
                              `${record.provider?.firstName ?? record.providerFirstName ?? ''} ${
                                  record.provider?.lastName ?? record.providerLastName ?? ''
                              }`.trim();
                          return (
                              <ProviderCell
                                  name={name}
                                  avatar={record.providerAvatar ?? record.provider?.avatar}
                                  credential={record.credential}
                              />
                          );
                      },
                  } as ColumnType<TPaymentBilling>,
              ]
            : []),
        {
            title: 'PATIENT CHARGE',
            dataIndex: 'amountCents',
            key: 'amountCents',
            width: 180,
            render: (amount: number) => {
                const val = amount || 0;
                return <div className={s.tableItem}>${(val / 100).toFixed(2)}</div>;
            },
            // Server-side across all pages (orderKey AMOUNT)
            sorter: true,
        },
        {
            title: 'STATUS',
            dataIndex: 'billingStatus',
            key: 'billingStatus',
            width: 180,
            render: (status: EBillingStatus, item: TPaymentBilling) => {
                return (
                    <StatusLabel
                        value={status}
                        isTextVisit={item.chargeType === EChargeType.TEXT_VISIT}
                        billingId={item.id}
                        isInvoice={isInvoice(item)}
                        item={item}
                    />
                );
            },
            filters: STATUS_FILTER_OPTIONS.map((o) => ({ text: o.label, value: o.label })),
            filteredValue: query.filters.billingStatus || null,
            onFilter: (value: any, record: TPaymentBilling) => {
                return true;
            },
        },
        {
            title: 'PAYOUT',
            dataIndex: 'payoutAmount',
            key: 'payoutAmount',
            width: 150,
            render: (payoutAmount: number) => {
                const val = payoutAmount || 0;
                return <div className={s.tableItem}>${val.toFixed(2)}</div>;
            },
            // Server-side across all pages (orderKey PAYOUT, empty payouts last)
            sorter: true,
        },
        // {
        //     title: "DESCRIPTION",
        //     dataIndex: "errorMessage",
        //     key: "errorMessage",
        //     width: 200,
        //     render: (errorMessage: string) => {
        //         return <div className={s.tableItem}>{errorMessage || "-"}</div>;
        //     },
        //     filterDropdown: (props: any) => (
        //         <div style={{ padding: 8 }}>
        //             <Input
        //                 placeholder="Search description"
        //                 value={searchDescriptionText}
        //                 onChange={(e) =>
        //                     setSearchDescriptionText(e.target.value)
        //                 }
        //                 onPressEnter={() => props.confirm()}
        //                 style={{ marginBottom: 8, display: "block" }}
        //             />
        //             <Space>
        //                 <Button
        //                     type="primary"
        //                     onClick={() => props.confirm()}
        //                     icon={<SearchOutlined />}
        //                     size="small"
        //                     style={{ width: 90 }}
        //                 >
        //                     Search
        //                 </Button>
        //                 <Button
        //                     onClick={() => {
        //                         setSearchDescriptionText("");
        //                         setFilteredInfo((prev) => ({
        //                             ...prev,
        //                             billingStatus: null,
        //                         }));
        //                         props.clearFilters();
        //                     }}
        //                     size="small"
        //                     style={{ width: 90 }}
        //                 >
        //                     Reset
        //                 </Button>
        //             </Space>
        //         </div>
        //     ),
        //     filterIcon: (filtered: boolean) => (
        //         <SearchOutlined
        //             style={{
        //                 color:
        //                     filtered || searchDescriptionText
        //                         ? "#1890ff"
        //                         : undefined,
        //             }}
        //         />
        //     ),
        //     onFilter: () => true, // 我们使用自己的过滤逻辑
        // },
        {
            title: 'TRANSACTION DATE',
            dataIndex: 'createdAt',
            width: 200,
            key: 'createdAt',
            render: (date: string) => {
                //03/26/2023
                return <div className={s.tableItem}>{dayjs(date).format('MM/DD/YYYY')}</div>;
            },

            // Server-side across all pages (orderKey CREATE_AT)
            sorter: true,
        },
        {
            title: 'ISSUE DATE',
            dataIndex: 'issueDate',
            key: 'issueDate',
            width: 200,
            render: (date: string) => {
                return <div className={s.tableItem}>{date ? dayjs(date).format('MM/DD/YYYY') : '-'}</div>;
            },
            // Server-side across all pages (orderKey ISSUE_DATE, empty dates last)
            sorter: true,
        },
        {
            title: 'DUE DATE',
            dataIndex: 'dueDate',
            key: 'dueDate',
            width: 200,
            render: (date: string) => {
                return <div className={s.tableItem}>{date ? dayjs(date).format('MM/DD/YYYY') : '-'}</div>;
            },
            // Server-side across all pages (orderKey DUE_DATE, empty dates last)
            sorter: true,
        },
        {
            title: 'INVOICE CREATED ON',
            dataIndex: 'createdAt',
            // Own key so antd doesn't treat it as the same column as TRANSACTION DATE
            key: 'invoiceCreatedAt',
            width: 200,
            render: (date: string) => {
                return <div className={s.tableItem}>{date ? dayjs(date).format('MM/DD/YYYY') : '-'}</div>;
            },
            // Server-side across all pages (orderKey CREATE_AT)
            sorter: true,
        },
        {
            title: '',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (record: TPaymentBilling, item: TPaymentBilling) => {
                const moreItems: any = [
                    canIssueRefund(record) && {
                        key: 'refund',
                        label: (
                            <div
                                onClick={() => {
                                    setCurrentItem(item);
                                    setShowIssueRefundModal(true);
                                }}
                                className={s.popItem}
                            >
                                Issue a refund
                            </div>
                        ),
                    },
                    isInvoice(item) &&
                        ![
                            EBillingStatus.FULLY_PAID,
                            EBillingStatus.CANCELED,
                            EBillingStatus.REFUND_PROCESSING,
                            EBillingStatus.PARTIAL_REFUNDED,
                            EBillingStatus.REFUNDED,
                        ].includes(record.billingStatus) && {
                            key: 'sendReminder',
                            label: <InvoiceReminderBtn item={item} />,
                        },
                    isInvoice(item) &&
                        [EBillingStatus.SENT, EBillingStatus.SCHEDULED].includes(record.billingStatus) && {
                            key: 'cancelInvoice',
                            label: (
                                <div
                                    className={s.popItem}
                                    onClick={() => {
                                        setCurrentItem(item);
                                        setShowCancelInvoiceModal(true);
                                    }}
                                >
                                    Cancel invoice
                                </div>
                            ),
                        },
                ].filter(Boolean);
                const showView =
                    record.billingStatus !== EBillingStatus.PROVIDER_NOT_READY &&
                    record.billingStatus !== EBillingStatus.PATIENT_NOT_READY;
                return (
                    <div className={s.tableActions}>
                        <Button
                            type="link"
                            disabled={!showView}
                            onClick={() => {
                                setCurrentItem(item);
                                if (isInvoice(item)) {
                                    setShowInvoiceDetailModal(true);
                                } else {
                                    setShowViewTransactionModal(true);
                                }
                            }}
                            className={s.viewButton}
                        >
                            View
                        </Button>
                        {moreItems.length > 0 && (
                            <Dropdown
                                menu={{
                                    items: moreItems,
                                }}
                                trigger={['hover']}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M12.0004 7.20039C10.6749 7.20039 9.60039 6.12587 9.60039 4.80039C9.60039 3.47491 10.6749 2.40039 12.0004 2.40039C13.3259 2.40039 14.4004 3.47491 14.4004 4.80039C14.4004 6.12587 13.3259 7.20039 12.0004 7.20039Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M12.0004 14.4004C10.6749 14.4004 9.60039 13.3259 9.60039 12.0004C9.60039 10.6749 10.6749 9.60039 12.0004 9.60039C13.3259 9.60039 14.4004 10.6749 14.4004 12.0004C14.4004 13.3259 13.3259 14.4004 12.0004 14.4004Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M9.60039 19.2004C9.60039 20.5259 10.6749 21.6004 12.0004 21.6004C13.3259 21.6004 14.4004 20.5259 14.4004 19.2004C14.4004 17.8749 13.3259 16.8004 12.0004 16.8004C10.6749 16.8004 9.60039 17.8749 9.60039 19.2004Z"
                                        fill="#6B7280"
                                    />
                                </svg>
                            </Dropdown>
                        )}
                    </div>
                );
            },
        },
    ];

    const columns = clientId ? allColumns.filter((col) => col.key !== 'providerFirstName') : allColumns;

    return (
        <div className={s.wrap}>
            <CommonTable
                className={`commonTable ${s.paymentTable}`}
                columns={columns}
                data={paymentBillingList || []}
                loading={loading}
                bordered
                rowKey="id"
                onChange={handleTableChange}
                pagination={{
                    current: query.current,
                    pageSize: 8,
                    total: paymentBillingListTotal,
                    showTotal: (total, range) => `${total} records`,
                }}
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: 'No earnings yet',
                }}
            />
            <RefundModal
                open={showIssueRefundModal}
                item={currentItem}
                isGroupPractice={isGroupPractice}
                onCancel={() => {
                    setShowIssueRefundModal(false);
                    setCurrentItem(undefined);
                }}
                onSuccess={() => {
                    setShowIssueRefundModal(false);
                    setCurrentItem(undefined);
                    refetchData();
                }}
            />
            <PaymentEventModal
                open={showViewTransactionModal}
                onCancel={() => {
                    setShowViewTransactionModal(false);
                }}
                item={currentItem}
            />
            <CancelInvoiceModal
                open={showCancelInvoiceModal}
                onCancel={() => {
                    setShowCancelInvoiceModal(false);
                }}
                item={currentItem!}
                onSuccess={() => {
                    setShowCancelInvoiceModal(false);
                    refetchData();
                }}
            />
            <InvoiceDetailModal
                open={showInvoiceDetailModal}
                onCancel={() => {
                    setShowInvoiceDetailModal(false);
                }}
                item={currentItem}
                refetchData={refetchData}
                isGroupPractice={isGroupPractice}
            />
        </div>
    );
};

export default PaymentTable;
